import {
  SftpIngestKind,
  SftpIngestMode,
  SftpOutboundReportType,
} from "types/SftpPartner";

export const SFTP_TAB_PATH = "/integrations?tab=sftp";

export const sftpPartnerPath = (slug: string) =>
  `/integrations/sftp/${encodeURIComponent(slug)}`;

export const SFTP_NEW_PARTNER_PATH = "/integrations/sftp/new";

export const sftpPartnerEditPath = (slug: string) =>
  `${sftpPartnerPath(slug)}/edit`;

// Mirrors the server's rules (sftp_partners.dto.ts); the server stays the authority.
export const SFTP_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const SFTP_PREFIX_RE = /^[^/\s][^\s]*\/$/;

export const ingestModeLabel = (mode: SftpIngestMode) =>
  mode === "PULL" ? "Pull" : "Push";

const INGEST_KIND_LABELS: Record<SftpIngestKind, string> = {
  EDI: "835/837 EDI",
  ATHENA_ORDERS: "Athena orders",
  NONE: "None",
};

/** What is done with a partner's inbound files. */
export const ingestKindLabel = (kind: SftpIngestKind) =>
  INGEST_KIND_LABELS[kind] ?? kind;

const OUTBOUND_REPORT_TYPE_LABELS: Record<SftpOutboundReportType, string> = {
  PLASTICS_PAYMENTS: "Plastics payments report",
};

export const outboundReportTypeLabel = (type: SftpOutboundReportType) =>
  OUTBOUND_REPORT_TYPE_LABELS[type] ?? type;

// Outgoing reports (CLE-2864). Mirror the server's rules (sftp_outbound.service.ts); the server stays
// the authority.
export const SFTP_FOLDER_RE = /^[^/\s][^\s]*\/$/;
export const SFTP_FILE_PREFIX_RE = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
// Folders the inbound imports read (the EDI sweep's 835/ and 837/, the Athena import's orders/). A
// report written there would be picked up as an inbound file.
export const SFTP_RESERVED_FOLDERS = ["835/", "837/", "orders/"];

export const sftpReservedFolderError = (folder: string): string | null =>
  SFTP_RESERVED_FOLDERS.includes(folder)
    ? `${folder} is a folder the import reads. Choose another folder (e.g. reports/).`
    : null;

const pad2 = (n: number) => String(n).padStart(2, "0");

/** The previous UTC calendar date as YYYY-MM-DD — the last day a file can cover. */
export const yesterdayUtc = (now: Date = new Date()): string => {
  const d = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1)
  );
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(
    d.getUTCDate()
  )}`;
};

/** YYYY-MM-DD → a Date at local midnight, so a Calendar shows that same day. */
export const isoDateToLocalDate = (iso: string): Date => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** A Calendar's (local) Date → the YYYY-MM-DD it shows. */
export const localDateToIsoDate = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const compactDate = (iso: string) => iso.slice(0, 10).split("-").join("");

/** The file name the server writes for a period (one date for one day, both for a range). */
export const outboundFileName = (
  fileNamePrefix: string,
  dateFrom: string,
  dateTo: string
) =>
  `${fileNamePrefix}_${compactDate(dateFrom)}${
    dateFrom.slice(0, 10) === dateTo.slice(0, 10)
      ? ""
      : `-${compactDate(dateTo)}`
  }.csv`;

/** A ledger row's period. The server sends dates as YYYY-MM-DD or as UTC-midnight timestamps. */
export const outboundPeriodLabel = (periodStart: string, periodEnd: string) => {
  const start = periodStart.slice(0, 10);
  const end = periodEnd.slice(0, 10);
  return start === end ? start : `${start} → ${end}`;
};

export const outboundSentByLabel = (triggeredByUserId: string | null) =>
  triggeredByUserId ? "SuperAdmin" : "Daily job";

/** 1536 → "1.5 KB". */
export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
};

const CLAIM_TYPE_LABELS: Record<string, string> = {
  INSTITUTIONAL: "Institutional",
  PROFESSIONAL_PROVIDER: "Professional",
  PROFESSIONAL_ANESTHESIA: "Anesthesia",
};

export const claimTypeLabel = (claimType: unknown): string => {
  if (typeof claimType !== "string" || !claimType) return "Unknown claim type";
  return CLAIM_TYPE_LABELS[claimType] || claimType;
};

/** `PRIMARY_PROVIDER` → `Primary provider`. */
export const humanizeEnum = (value: string | null | undefined): string => {
  if (!value) return "";
  const words = value.toLowerCase().split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** "3 hours ago" style, for the list's Last file processed column (exact time on hover). */
export const relativeTime = (
  iso: string | null | undefined,
  now: Date = new Date()
): string => {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const units: [number, string][] = [
    [60 * 60 * 24 * 365, "year"],
    [60 * 60 * 24 * 30, "month"],
    [60 * 60 * 24, "day"],
    [60 * 60, "hour"],
    [60, "minute"],
  ];
  const [size, unit] = units.find(([s]) => seconds >= s) || units[4];
  const n = Math.floor(seconds / size);
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
};

// Rule fields shown in the rule's main line (and edited by the form); everything else is listed
// under it as the rule's other settings.
export const RULE_HEADLINE_FIELDS = [
  "claimType",
  "providerId",
  "placeOfServiceFilter",
];

export interface IPayerTaxonomyRuleView {
  payerIds: number[];
  includeAll: boolean;
  taxonomies: string[];
}

export const isPayerTaxonomyRuleList = (
  value: unknown
): value is IPayerTaxonomyRuleView[] =>
  Array.isArray(value) &&
  value.every(
    (r) =>
      r &&
      typeof r === "object" &&
      Array.isArray((r as IPayerTaxonomyRuleView).payerIds) &&
      typeof (r as IPayerTaxonomyRuleView).includeAll === "boolean" &&
      Array.isArray((r as IPayerTaxonomyRuleView).taxonomies)
  );

/** A payer's name from the detail response, or `Payer <id>` when it has no Payer row. */
export const payerLabel = (
  id: number,
  payerNames: Record<string, string> = {}
): string => payerNames[String(id)] ?? `Payer ${id}`;

export const describePayerTaxonomyRule = (
  r: IPayerTaxonomyRuleView,
  payerNames: Record<string, string> = {}
) => {
  const payers = r.payerIds.map((id) => payerLabel(id, payerNames)).join(", ");
  if (r.includeAll) return `${payers}: every rendering taxonomy`;
  if (!r.taxonomies.length) return `${payers}: no taxonomies (none priced)`;
  return `${payers}: only ${r.taxonomies.join(", ")}`;
};

/** RTK Query error → the server's message, for the toast. */
export const sftpApiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { data?: { message?: unknown } })?.data;
  return typeof data?.message === "string" && data.message.length > 0
    ? data.message
    : fallback;
};
